import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import Header from './components/Header';
import MainContent from './components/MainContent';
import './index.css';

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("theme") !== "light";
  });
  const [currentRoute, setCurrentRoute] = useState(window.location.pathname || "/");
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Standard History Routing & Popstate Listener
  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(window.location.pathname || "/");
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Global Anchor Click Interceptor for Clean SPA Routing
  useEffect(() => {
    const handleGlobalClick = (e) => {
      const anchor = e.target.closest("a");
      if (
        anchor &&
        anchor.href &&
        anchor.host === window.location.host &&
        !anchor.getAttribute("download") &&
        anchor.getAttribute("target") !== "_blank"
      ) {
        e.preventDefault();
        const path = anchor.pathname;
        if (window.location.pathname !== path) {
          window.history.pushState(null, "", path);
          setCurrentRoute(path);
        }
      }
    };
    document.addEventListener("click", handleGlobalClick);
    return () => {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, []);

  // Trigger 300ms loading effect on route changes for premium app boot feeling
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentRoute]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main: isDarkMode ? '#90caf9' : '#1976d2', // Professional Blue
          },
          secondary: {
            main: '#f48fb1',
          },
          background: {
            default: isDarkMode ? '#0f172a' : '#f8fafc', // Slate 900 / Slate 50
            paper: isDarkMode ? '#1e293b' : '#ffffff',   // Slate 800 / White
          },
          text: {
            primary: isDarkMode ? '#f8fafc' : '#0f172a',
            secondary: isDarkMode ? '#94a3b8' : '#64748b',
          }
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h6: {
            fontWeight: 600,
            letterSpacing: '0.5px'
          }
        },
        shape: {
          borderRadius: 8, // More professional, slightly sharper corners than 12
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                color: isDarkMode ? '#f8fafc' : '#0f172a',
                backgroundImage: 'none',
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                backgroundImage: 'none',
                borderRight: isDarkMode ? '1px solid #1e293b' : '1px solid #e2e8f0',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                boxShadow: isDarkMode ? '0 4px 6px -1px rgb(0 0 0 / 0.5)' : '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                border: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0',
              }
            }
          }
        }
      }),
    [isDarkMode],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {isLoading && (
          <Box sx={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backgroundColor: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)',
            zIndex: 9999, backdropFilter: 'blur(4px)'
          }}>
            <CircularProgress color="primary" />
            <Typography variant="overline" sx={{ mt: 2, letterSpacing: 2 }}>Loading Workspace...</Typography>
          </Box>
        )}
        
        <Header 
          isDarkMode={isDarkMode} 
          setIsDarkMode={setIsDarkMode} 
          search={search} 
          setSearch={setSearch}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
        
        <MainContent 
          search={search} 
          setSearch={setSearch}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          currentRoute={currentRoute}
        />
      </Box>
    </ThemeProvider>
  );
}

export default App;
